import { BookOpen, ExternalLink, PlayCircle, ArrowRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { DisclaimerBanner } from "../components/DisclaimerBanner";
import { ARTICLES } from "../data/articles";

const QUICK_GUIDES = [
  { title: "How tolerance & dependence develop", body: "With regular use, the brain adapts to benzodiazepines. Over time the same dose has less effect (tolerance), and the body comes to rely on the drug to feel normal (dependence). This is a physical process, not a personal weakness — it can happen even when taking medication exactly as prescribed." },
  { title: "What a safe taper looks like", body: "A safe taper is slow and gradual — often reducing by around 10% of the current dose every 1–2 weeks, with hold periods when symptoms flare. Longer-acting benzodiazepines like diazepam are often used to smooth the process. There is no single 'correct' speed; the right pace is the one you and your prescriber can sustain comfortably." },
  { title: "Protracted withdrawal", body: "For some people, symptoms linger for months after the last dose. This is sometimes called protracted withdrawal, and it usually improves over time in 'windows and waves' — good days followed by harder ones. It is real, it is temporary, and it does not mean you've done anything wrong." },
  { title: "Sleep hygiene during a taper", body: "Sleep often gets harder during a taper. Keeping a consistent bedtime, dimming lights in the evening, avoiding caffeine after midday, and gentle wind-down routines can all help. Broken sleep is one of the most common taper symptoms and it does ease as your nervous system settles." },
  { title: "Talking to your GP about tapering", body: "Bring your reference schedule and be clear that you want to reduce gradually and safely. The GP letter feature in TaperPath can help start the conversation. Ask about switching to a longer-acting benzodiazepine, and agree a plan you can revisit together if symptoms become difficult." },
  { title: "What to expect emotionally", body: "Anxiety, low mood and emotional sensitivity are common as your brain rebalances. These feelings are part of the process, not a sign of failure. Support — from a buddy, a group, or a professional — makes a real difference. Be as kind to yourself as you would be to a friend." },
];

const FAQ = [
  { q: "Is it dangerous to stop suddenly?", a: "Yes — stopping benzodiazepines abruptly can cause severe withdrawal, including seizures. Always reduce gradually under medical supervision." },
  { q: "Is TaperPath a substitute for my doctor?", a: "No. TaperPath is an educational harm-reduction tool. All tapering should be agreed and supervised by a doctor or prescriber." },
  { q: "How long does tapering take?", a: "It varies from person to person — often several months. Slower is usually gentler. Your prescriber will help you find a pace that works." },
  { q: "Is my data private?", a: "Yes. Your health data is private by default, never sold or shared, and you can delete your account and all data at any time." },
  { q: "What if I have a harder day and increase my dose?", a: "That's okay. A setback isn't a failure — it's part of many people's journeys. TaperPath will never shame you or reset your progress to zero." },
];

const VOICES = [
  { name: "Benzo Recovery Official", desc: "Real recovery stories and support on TikTok.", url: "https://www.tiktok.com/@benzorecovery" },
  { name: "Path 2 Recovery", desc: "Peer-led recovery resources and community.", url: "https://changeyourtomorrow.co.uk" },
  { name: "The Ashton Manual", desc: "The definitive clinical reference on benzodiazepine tapering.", url: "https://benzo.org.uk/manual" },
];

export default function Education() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-head text-3xl font-bold text-[#2C363F] sm:text-4xl">Learn</h1>
        <p className="mt-2 text-muted-foreground">Plain-English guides, answers to common questions, and real recovery voices.</p>
      </div>

      <nav data-testid="learn-anchor-nav" className="sticky top-16 z-30 -mx-4 flex flex-wrap gap-2 border-b border-border bg-background/80 px-4 py-3 backdrop-blur sm:mx-0 sm:rounded-2xl sm:border sm:px-4">
        {[
          { id: "articles", label: "Articles" },
          { id: "quick-guides", label: "Quick guides" },
          { id: "faqs", label: "FAQs" },
          { id: "voices", label: "Community voices" },
        ].map((s) => (
          <a key={s.id} href={`#${s.id}`} data-testid={`anchor-${s.id}`}
            className="rounded-full border border-[#4A7c59]/20 bg-white px-4 py-1.5 text-sm font-medium text-[#4A7c59] transition-colors duration-200 hover:bg-[#4A7c59]/10">
            {s.label}
          </a>
        ))}
      </nav>

      <DisclaimerBanner />

      <div id="articles" className="scroll-mt-32">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-head text-2xl font-bold text-[#2C363F]">In-depth articles</h2>
            <p className="text-sm text-muted-foreground">Longer reads on the benzo landscape, tapering and recovery.</p>
          </div>
          <Link to="/articles" data-testid="learn-all-articles" className="hidden shrink-0 items-center gap-1 text-sm font-medium text-[#4A7c59] hover:underline sm:flex">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="learn-articles-grid">
          {ARTICLES.map((a) => (
            <Link key={a.slug} to={`/articles/${a.slug}`} data-testid={`learn-article-${a.slug}`}
              className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-sm transition-transform duration-300 hover:-translate-y-1">
              <div className="h-36 overflow-hidden"><img src={a.image} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
              <div className="flex flex-1 flex-col p-5">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <span className="rounded-full bg-[#4A7c59]/10 px-2 py-0.5 text-[#4A7c59]">{a.category}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {a.readTime}</span>
                </div>
                <h3 className="mt-2 font-head text-base font-semibold leading-snug text-[#2C363F]">{a.title}</h3>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#4A7c59]">Read <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div id="quick-guides" className="scroll-mt-32">
        <h2 className="font-head text-2xl font-bold text-[#2C363F]">Quick guides</h2>
        <div className="mt-4 grid gap-5 sm:grid-cols-2">
        {QUICK_GUIDES.map((a) => (
          <div key={a.title} className="rounded-2xl border border-border bg-white p-6 shadow-sm">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#4A7c59]/10 text-[#4A7c59]"><BookOpen strokeWidth={1.8} className="h-5 w-5" /></div>
            <h3 className="font-head text-lg font-semibold text-[#2C363F]">{a.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.body}</p>
          </div>
        ))}
      </div>
      </div>

      <div id="faqs" className="scroll-mt-32">
        <h2 className="font-head text-2xl font-bold text-[#2C363F]">Frequently asked questions</h2>
        <div className="mt-4 rounded-2xl border border-border bg-white p-2 shadow-sm">
          <Accordion type="single" collapsible>
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`i${i}`} data-testid={`faq-${i}`}>
                <AccordionTrigger className="px-4 text-left font-head font-medium text-[#2C363F]">{f.q}</AccordionTrigger>
                <AccordionContent className="px-4 text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      <div id="voices" className="scroll-mt-32">
        <h2 className="font-head text-2xl font-bold text-[#2C363F]">Community voices</h2>
        <p className="text-sm text-muted-foreground">Trusted content and advocates. You're not walking this path alone.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          {VOICES.map((v) => (
            <a key={v.name} href={v.url} target="_blank" rel="noreferrer" data-testid="voice-link" className="group rounded-2xl border border-border bg-white p-6 shadow-sm transition-transform duration-300 hover:-translate-y-1">
              <PlayCircle className="h-8 w-8 text-[#E8907E]" />
              <h3 className="mt-3 font-head font-semibold text-[#2C363F]">{v.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{v.desc}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#4A7c59]">Visit <ExternalLink className="h-3.5 w-3.5" /></span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
